import { useMutation, useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useCallback } from 'react';
import { useApolloFederation } from '../apollo/ApolloFederationClient';

// Inline minimal documents to avoid codegen dependency here
const LIST = gql`
  query ListComposition($tenant: String!, $name: String!, $version: String) {
    listComponents(tenant: $tenant, name: $name, version: $version) { id component_type name version_pin execution_mode input_map output_map config metadata }
    listComponentEdges(tenant: $tenant, name: $name, version: $version) { id parent_component_id child_component_id relationship condition order fanout_group metadata }
  }
`;

const ADD = gql`
  mutation AddComponent($input: AddComponentInput!) { addComponent(input: $input) { id component_type name version_pin execution_mode input_map output_map config metadata } }
`;

const CONNECT = gql`
  mutation ConnectComponents($input: ConnectComponentsInput!) { connectComponents(input: $input) { id parent_component_id child_component_id relationship condition order fanout_group metadata } }
`;

const REMOVE = gql`
  mutation RemoveComponent($input: RemoveComponentInput!) { removeComponent(input: $input) }
`;

const VALIDATE = gql`
  mutation ValidateComposition($tenant: String!, $name: String!, $version: String) { validateComposition(tenant: $tenant, name: $name, version: $version) { ok errors issues } }
`;

export interface UseWorkflowCompositionArgs { tenant: string; name: string; version?: string }

export const useWorkflowComposition = ({ tenant, name, version }: UseWorkflowCompositionArgs) => {
  const { client } = useApolloFederation();

  const { data, loading, error, refetch } = useQuery<Record<string, any>>(LIST, { variables: { tenant, name, version }, client, fetchPolicy: 'cache-and-network' });
  const [addComponentMut] = useMutation<Record<string, any>>(ADD, { client });
  const [connectComponentsMut] = useMutation<Record<string, any>>(CONNECT, { client });
  const [removeComponentMut] = useMutation<Record<string, any>>(REMOVE, { client });
  const [validateMut] = useMutation<Record<string, any>>(VALIDATE, { client });

  const addComponent = useCallback(async (input: any) => {
    const res = await addComponentMut({ variables: { input } });
    await refetch();
    return res.data?.addComponent;
  }, [addComponentMut, refetch]);

  const connectComponents = useCallback(async (input: any) => {
    const res = await connectComponentsMut({ variables: { input } });
    await refetch();
    return res.data?.connectComponents;
  }, [connectComponentsMut, refetch]);

  const removeComponent = useCallback(async (id: string) => {
    const res = await removeComponentMut({ variables: { input: { id } } });
    await refetch();
    return !!res.data?.removeComponent;
  }, [removeComponentMut, refetch]);

  const validate = useCallback(async () => {
    const res = await validateMut({ variables: { tenant, name, version } });
    return res.data?.validateComposition;
  }, [validateMut, tenant, name, version]);

  const components = data?.listComponents ?? [];
  const edges = data?.listComponentEdges ?? [];

  return { components, edges, loading, error, addComponent, connectComponents, removeComponent, validate, refetch };
};


